import React from 'react';
import { Header } from './Header';
import { QuoteForm } from './QuoteForm';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { vtcData } from '../mock';
import { 
  Plane, Train, Camera, Heart, CheckCircle, Phone, 
  MapPin, Clock, Star, Shield, Car, Calendar, MessageCircle 
} from 'lucide-react';

const iconMap = {
  Plane, Train, Camera, Heart, Clock
};

export const VTCHome = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.8), rgba(15, 23, 42, 0.8)), url('/images/chateau-fontainebleau.jpg')`
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-blue-600/80 text-blue-100 hover:bg-blue-600/80">
              Service VTC Professionnel à Fontainebleau
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Votre Transport
              <br />
              <span className="text-blue-400">de Confiance</span>
            </h1>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Transport privé avec chauffeur professionnel depuis Fontainebleau. 
              Transferts aéroports, gares et tous vos déplacements en toute sérénité.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg"
                onClick={() => scrollToSection('devis')}
              >
                <Calendar className="mr-2" size={20} />
                Réserver Maintenant
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="border-2 border-blue-400 text-blue-400 hover:bg-blue-900 px-8 py-4 text-lg"
              >
                <Phone className="mr-2" size={20} />
                06 69 47 52 16
              </Button>
            </div>
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-blue-400/20 rounded-full animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-16 h-16 bg-gray-600/20 rounded-full animate-pulse delay-1000"></div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Nos Services
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Transport professionnel pour tous vos besoins de déplacement
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {vtcData.services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 border-gray-700 bg-gray-900 shadow-lg">
                  <CardHeader className="text-center pb-4">
                    <div className="w-16 h-16 bg-blue-900/50 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-600 transition-colors">
                      <IconComponent className="text-blue-400 group-hover:text-white transition-colors" size={28} />
                    </div>
                    <CardTitle className="text-lg font-semibold text-white">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-center text-gray-300">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section id="avantages" className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Pourquoi Choisir un VTC ?
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Les avantages du transport avec chauffeur professionnel
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {vtcData.advantages.map((advantage) => (
              <div key={advantage.id} className="flex items-start space-x-4 bg-gray-800 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-700">
                <div className="flex-shrink-0">
                  <CheckCircle className="text-green-400" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {advantage.title}
                  </h3>
                  <p className="text-gray-300">
                    {advantage.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Vehicle Info */}
          <div 
            className="mt-16 bg-cover bg-center bg-no-repeat rounded-2xl shadow-lg p-8 max-w-3xl mx-auto relative"
            style={{
              backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.9), rgba(15, 23, 42, 0.9)), url('/images/voiture-luxe-1.jpg')`
            }}
          >
            <div className="flex items-center justify-center mb-6">
              <Car className="text-blue-400 mr-3" size={32} />
              <h3 className="text-2xl font-bold text-white">Notre Véhicule</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <Shield className="text-blue-400 mx-auto mb-2" size={24} />
                <p className="font-semibold text-white">Berline Récente</p>
                <p className="text-sm text-gray-300">Véhicule entretenu et assuré</p>
              </div>
              <div>
                <Star className="text-blue-400 mx-auto mb-2" size={24} />
                <p className="font-semibold text-white">Grand Confort</p>
                <p className="text-sm text-gray-300">Climatisation, sièges cuir</p>
              </div>
              <div>
                <Clock className="text-blue-400 mx-auto mb-2" size={24} />
                <p className="font-semibold text-white">Ponctualité</p>
                <p className="text-sm text-gray-300">Suivi GPS en temps réel</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form Section */}
      <section 
        id="devis" 
        className="py-20 bg-cover bg-center bg-no-repeat relative"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.95), rgba(15, 23, 42, 0.95)), url('/images/tour-eiffel-1.jpg')`
        }}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Demander un Devis
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Recevez votre tarification personnalisée en quelques clics
            </p>
          </div>
          <QuoteForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          {/* SEO catchphrases */}
          <div className="text-center mb-12 space-y-4">
            <p className="text-lg text-gray-300 font-medium">
              "Alternative aux taxis à Fontainebleau et ses environs."
            </p>
            <p className="text-lg text-gray-300 font-medium">
              "Transport privé : le confort d'un taxi au prix d'un VTC."
            </p>
            <p className="text-lg text-gray-300 font-medium">
              "Besoin d'un taxi pour la gare de Fontainebleau-Avon ? Réservez votre chauffeur privé"
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-gray-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">M</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">VTC Fontainebleau</h3>
                  <p className="text-gray-400 text-sm">Transport avec Chauffeur</p>
                </div>
              </div>
              <p className="text-gray-400">
                Service de transport avec chauffeur professionnel depuis Fontainebleau.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Phone size={16} />
                  <span>06 69 47 52 16</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MessageCircle size={16} />
                  <a 
                    href="https://wa.me/33669475216" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-green-400 hover:text-green-300 transition-colors"
                  >
                    WhatsApp
                  </a>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin size={16} />
                  <span>Fontainebleau, Seine-et-Marne</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Transferts Aéroports</li>
                <li>Transferts Gares</li>
                <li>Lieux Touristiques</li>
                <li>Établissements Médicaux</li>
                <li>Mise à Disposition</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 VTC Fontainebleau. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};